import React from 'react';
import { Space } from 'antd';
import styles from './HPTitle.module.css';

/**
 * HPTitle - Page Title Component
 * 
 * Displays a formatted page title with optional subtitle and additional content.
 * Used as a consistent header for educational pages and course sections.
 * 
 * The component provides a two-column layout:
 * - Left: Title information (name + subname)
 * - Right: Optional children content (buttons, controls, etc.)
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {string} props.name - Main title text (displayed as small label)
 * @param {string} props.subname - Subtitle or description (displayed as larger paragraph)
 * @param {React.ReactNode} [props.children] - Optional additional content displayed on the right
 * @param {Function} [props.onTitleClick] - Optional click handler for the title area (makes it interactive)
 * 
 * @returns {JSX.Element} Formatted title section with responsive layout
 * 
 * @example
 * // Basic title
 * <HPTitle name="Chapter 1" subname="Introduction to Heat Pumps" />
 * 
 * @example
 * // Title with action button
 * <HPTitle name="Simulation" subname="Heat Transfer Visualization">
 *   <Button>Start Simulation</Button>
 * </HPTitle>
 * 
 * @example
 * // Clickable title for navigation
 * <HPTitle 
 *   name="1. Cold Room" 
 *   subname="1.1 Heat Loss"
 *   onTitleClick={() => setShowCourseNav(true)}
 * />
 */
export function HPTitle({ name, subname, children, onTitleClick }) {
  return (
    <div className={styles.titleSection}>
      {/* Use Ant Design Space for flexible layout with proper spacing */}
      <Space className="default-opposite-arrangement" align="start">
        {/* Title and subtitle container - clickable if handler provided */}
        <div 
          className={`${styles.titleInfo} ${onTitleClick ? styles.clickable : ''}`}
          onClick={onTitleClick}
          role={onTitleClick ? 'button' : undefined}
          tabIndex={onTitleClick ? 0 : undefined}
          onKeyDown={onTitleClick ? (e) => e.key === 'Enter' && onTitleClick() : undefined}
        >
          <small className={styles.name}>{name}</small>
          <p className={styles.subname}>{subname}</p>
        </div>
        
        {/* Optional children content (conditionally rendered) */}
        {children && <div className={styles.content}>{children}</div>}
      </Space>
    </div>
  );
}

export default HPTitle;
